import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { Autocomplete } from 'components/FormFields';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { truncateKeywordSpaces } from 'utils/utils';

const VehicleGroupsTimezonesSelect = ({
  label,
  variant,
  field: { name, value, onChange },
  style,
  datacy,
  form: { errors, touched, setFieldValue },
  disabled,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });
  const { loading, timezones } = useSelector((state) => state.vehiclesGroups);

  /**
   * Fetch vehicles list
   * @param {*} params
   */
  const fetchVehicleGroupsTimezones = useCallback(
    debounce((params = {}) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(VehiclesGroupsActions.fetchVehicleGroupsTimezones(newParams));
    }, 1000),
    []
  );

  useEffect(() => {
    if (!value) return;
    fetchVehicleGroupsTimezones({ ...params });
  }, []) // eslint-disable-line

  useEffect(() => {
    if (value || value === '') {
      const defaultVal = timezones.find((el) => el.time_zone === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(value);
      }
    }
  }, [value, timezones]) // eslint-disable-line

  const handleFetchVehicleGroupsTimezones = () => {
    if (timezones.length === 0 && !loading && !params?.keyword)
      dispatch(VehiclesGroupsActions.fetchVehicleGroupsTimezones(params));
  };

  return (
    <Autocomplete
      name={name}
      style={style}
      label={label}
      margin="none"
      dataCy={datacy}
      fullwidth="true"
      variant={variant}
      loading={loading}
      onHighlightChange={handleFetchVehicleGroupsTimezones}
      options={timezones}
      disabled={disabled}
      value={defaultValue}
      id="VehicleGroupsTimezones"
      getOptionLabel={(option) => `${option?.time_zone || ''}`}
      error={errors && errors[name] && touched && touched[name]}
      onChange={(ev, val) => {
        setFieldValue(name, val?.time_zone, val || '');
        if (onChange) onChange(name, val?.time_zone, val || '');
      }}
      getOptionSelected={(option, value) =>
        option.time_zone === value.time_zone || option.time_zone === value
      }
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

VehicleGroupsTimezonesSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  style: PropTypes.object,
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

VehicleGroupsTimezonesSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
    setFieldValue: () => {},
  },
  style: {},
  customParams: {},
  label: 'Select...',
  variant: 'standard',
  datacy: 'vg-timezones-select',
};

export default VehicleGroupsTimezonesSelect;
